/*
    This will be the Job Control Page

    Links here 
        Add Job
        AGM Planning
        AGM Edit
        Subchain Edit
        Run Control
        Speed Notifications

*/

import React, { useEffect, useState } from 'react'
//ui components
import { Button } from "@components/ui/buttons/Button"
import { PageHeader } from '@components/ui/layout/PageHeader'
import { PageContainer } from "@components/ui/layout/PageContainer"
import Label from '@components/ui/labels/Label'
import { SelectContainer } from '@components/ui/layout/SelectContainer'
import Select from "react-select"
import { TabButton } from '@components/ui/tabNavigation/TabButton'
import { TabContainer } from '@components/ui/tabNavigation/TabContainer'
//page components
import { JobDetails } from "./components/JobDetails/JobDetails";
import { AGMEdit } from "./components/AgmEdit/AGMEdit"
import { AGMPlanning } from "./components/AGMPlanning/AGMPlanning"
import { RunControl } from "./components/RunControl/RunControl"
import { SubChainEdit } from "./components/SubChainEdit/SubChainEdit"
import { SpeedNotifications } from "./components/SpeedNotifications/SpeedNotifications"
import { ArchiveRun } from './components/ArchiveRun/ArchiveRun'
//hooks
import useJobOptions from "@hooks/useJobOptions";
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext'
import { useError } from '@contexts/ErrorContext'
import { useUser } from '@contexts/UserContext'
//utils
import { fetchJobData } from '@utils/jobs/fetchJobData'
import { useLoading } from '@contexts/LoadingContext'
import { MissingItemsIndicator } from '@components/ui/tabNavigation/MissingItemsIndicator'

/**
 * Parent component where users can create/update/read/delete job related data, including: AGMs, Subchains, Runs, Speed Notifications
 * 
 * @returns {JSX.Element}
 * 
 * @category Pages
 */
export const JobControl = () => {
    const [activeComponent, setActiveComponent] = useState('JobDetails')
    const [selectedJob, setSelectedJob] = useState("")

    const {jobNameOptions, setJobNameOptions} = useJobOptions()
    const {showError} = useError()
    const {hasUnsavedChanges, setHasUnsavedChanges, requestConfirm} = useUnsavedChanges()
    const {storedJob, setStoredJob} = useUser()
    const {showLoading, hideLoading} = useLoading()

    //render components depending on which button selected
    const componentMap = {
        JobDetails: <JobDetails selectedJob={selectedJob} setSelectedJob={setSelectedJob} jobNameOptions={jobNameOptions} setJobNameOptions={setJobNameOptions}/>,
        AGMPlanning: <AGMPlanning selectedJob={selectedJob} setActiveComponent={setActiveComponent} />,
        AGMEdit: <AGMEdit selectedJob={selectedJob} setSelectedJob={setSelectedJob} />,
        SubChainEdit: <SubChainEdit selectedJob={selectedJob} />,
        RunControl: <RunControl selectedJob={selectedJob}/>,
        SpeedNotifications: <SpeedNotifications selectedJob={selectedJob}/>,
        ArchiveRun: <ArchiveRun selectedJob={selectedJob}/>
    };

    //handle navigation - check if there are unsaved changes first
    const handleNavigation = async (component) => {
        if(component === activeComponent) return
        
        //if there is unsaved work, ask user to confirm if they want to continue
        if(hasUnsavedChanges){
            const isConfirmed = await requestConfirm("You have unsaved work - are you sure you want to leave this page?")
        
            if(!isConfirmed) return
        }

        setActiveComponent(component)
        setHasUnsavedChanges(false)
        fetchJobData(storedJob, showError, setSelectedJob, setStoredJob, setHasUnsavedChanges)
    };

    const handleChangeSelectedJob = async(selectedJob) => {
        showLoading("Getting job...")

        //if there is unsaved work, ask user to confirm if they want to continue
        if(hasUnsavedChanges){
            const isConfirmed = await requestConfirm("You have unsaved work - are you sure you want to leave this page?")
        
            if(!isConfirmed) return
        }

        await fetchJobData(selectedJob, showError, setSelectedJob, setStoredJob, setHasUnsavedChanges)

        hideLoading()
    }

    const handleCreateJobClick = async() => {
        if(hasUnsavedChanges){
            const isConfirmed = await requestConfirm("You have unsaved work - are you sure you want to leave this page?")
        
            if(!isConfirmed) return
        }
        setHasUnsavedChanges(false)

        setActiveComponent("JobDetails")
        setSelectedJob("")
        setStoredJob(null)
    }


    useEffect(() => {
        document.title =  `MarTrak - Job Control`

        //if a job was previously selected and added to UserContext, pull it in here
        if(storedJob){
            setSelectedJob(storedJob)
        }

    }, [storedJob]);

    useEffect(() => {
        if(!storedJob) return
        //fetch job data on page load
        //POI, AGM, Subchain counts or IsLocked may have changed since job originally stored in user context
        fetchJobData(storedJob, showError, setSelectedJob, setStoredJob, setHasUnsavedChanges)
    }, []) //eslint-disable-line

    return (
        <>
            {/* header */}

            <PageHeader pageTitle="Job Control"/>

            {/* page content */}

            <PageContainer>

                {/* job dropdown */}
                <Label htmlFor='jobDropdown'>Select Job</Label>

                <div className='flex flex-col md:flex-row md:items-center w-full'>

                    <SelectContainer>
                        <Select
                            value={selectedJob}
                            options={jobNameOptions}
                            name="jobDropdown"
                            onChange={(x) => handleChangeSelectedJob(x)}
                            menuPosition='fixed'
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </SelectContainer>

                    <div>
                        <Button
                            onClick={() => handleCreateJobClick()}
                            variant="success"
                            className='md:ml-4 mb-8'
                        >
                            Create New Job
                        </Button>
                    </div>

                </div>
                
                {/* tab-style buttons to toggle components */}

                <TabContainer>
                    <TabButton 
                        onClick={() => handleNavigation("JobDetails")} 
                        isActive={activeComponent === "JobDetails"}
                    >
                        Job Details
                    </TabButton>

                    {/* TODO: if we decide that we don't need AGM Planning component, remove the code. Until then, wait for feedback on necessity of the component. Reasoning for commenting this out: we can do everything on AGM Edit that we can do on AGM Planning, seems like unnecessary step. */}
{/* 
                    <TabButton 
                        onClick={() => handleNavigation("AGMPlanning")} 
                        isActive={activeComponent === "AGMPlanning"}
                    >
                        AGM Planning
                    </TabButton> */}

                    <TabButton 
                        onClick={() => handleNavigation("AGMEdit")} 
                        isActive={activeComponent === "AGMEdit"}
                    >
                        AGM Edit
                        <MissingItemsIndicator 
                            showIndicator={storedJob?.AGMCount === 0}
                            tooltipLabel="No AGMs have been added"
                            className='text-red-500'
                        />
                    </TabButton>

                    <TabButton 
                        onClick={() => handleNavigation("SubChainEdit")} 
                        isActive={activeComponent === "SubChainEdit"}
                    >
                        Subchain Edit
                        <MissingItemsIndicator 
                            showIndicator={storedJob?.SubchainCount === 0}
                            tooltipLabel="No subchains have been added"
                        />
                    </TabButton>

                    <TabButton 
                        onClick={() => handleNavigation("RunControl")} 
                        isActive={activeComponent === "RunControl"}
                    >
                        Run Control
                        <MissingItemsIndicator 
                            showIndicator={storedJob?.RunCount === 0}
                            tooltipLabel="No runs have been added"
                        />
                    </TabButton>

                    <TabButton 
                        onClick={() => handleNavigation("SpeedNotifications")} 
                        isActive={activeComponent === "SpeedNotifications"}
                    >
                        Speed Notifications
                        <MissingItemsIndicator 
                            showIndicator={storedJob ? Object.values(storedJob.notificationCount).some(r => r.SpeedNotificationCount === 0) : false}
                            tooltipLabel="Some runs do not have speed notifications set up"
                        />
                    </TabButton>

                    <TabButton 
                        onClick={() => handleNavigation("ArchiveRun")} 
                        isActive={activeComponent === "ArchiveRun"}
                    >
                        Archive Run
                    </TabButton>

                </TabContainer>

                {/* display component depending on user selection */}
                <div>
                    {selectedJob.IsLocked && selectedJob.POICount > 0 ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 text-sm text-amber-700 rounded-lg my-8 max-w-max">
                            <i className="fa-solid fa-lock mr-2 text-amber-600" ></i>
                           This job is locked and has POIs. To unlock the job and edit AGMs or Subchains, all POIs must be deleted.
                        </div>
                    ) : activeComponent !== 'JobDetails' && selectedJob.IsLocked && selectedJob.POICount === 0 ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 text-sm text-amber-700 rounded-lg my-8 max-w-max">
                            <i className="fa-solid fa-lock mr-2 text-amber-600" ></i>
                           This job is locked. Unlock in Job Details to change AGMs and Subchains.
                        </div>
                    ) : selectedJob.IsLocked === false ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 text-sm text-amber-700 rounded-lg my-8 max-w-max">
                            <i className="fa-solid fa-unlock mr-2 text-amber-600" ></i>
                           This job is unlocked. Job must be locked to view POIs, Live Track, Live Track (edit), and Site Map w/PIG. {activeComponent !== 'JobDetails' && 'When AGMs and Subchains are complete, go to Job Details to lock the job.'}
                        </div>
                    ) : null}
                    {componentMap[activeComponent]}
                </div>

            </PageContainer>

        </>
    )
}